import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getMockHomeworkSession, isMockHomeworkContext } from '@/utils/homeworkSession';
import { getStudentInfo } from '@/api/start';
import { getRemainingPoints } from '@/api/points';

export interface IUSER {
  inited: boolean;
  userId: string;
  userName: string;
  avatar: string;
  vipDays: number;
  readingVipDays: number;
  listeningVipDays: number;
  userPermission: {
    isLogin: boolean;

    isVip: boolean;
    expirationTime: number;

    points: number;
    writing: {
      isVip: boolean;
    };
    reading: {
      isVip: boolean;
    };
    listening: {
      isVip: boolean;
    };
  };
  targetScore: {
    reading: number;
    writing: number;
    listening: number;
  };
  businessBrand?: {
    orgKey: string;
    logoUrl: string;
  };
}

const initialState: IUSER = {
  inited: false,
  userId: '',
  userName: '',
  avatar: '',
  vipDays: 0,
  readingVipDays: 0,
  listeningVipDays: 0,
  businessBrand: undefined,
  userPermission: {
    isLogin: false,
    isVip: false,
    points: 0,
    expirationTime: 0,
    writing: {
      isVip: false,
    },
    reading: {
      isVip: false,
    },
    listening: {
      isVip: false,
    }
  },
  targetScore: {
    reading: 6.5,
    writing: 6.5,
    listening: 6.5,
  },
};

export const getStudentInfoAsync = createAsyncThunk('reading/getStudentInfo', async (params, {rejectWithValue, getState}) => {
  try {
    const res = await getStudentInfo();
    if (res.code !== 200) {
      return rejectWithValue(null);
    }
    const role = res.header ? res.header['x-user-role'] : undefined;
    return {
      ...res.data,
      // A successful scoped profile authenticates only this practice session.
      // Teacher transport deliberately omits the ordinary student auth headers.
      isLogin: !!getMockHomeworkSession() || (!!role && role !== 'tourist'),
    };
  } catch (err) {
    return rejectWithValue(null);
  }
});


export const fetchUserPoints2 = createAsyncThunk('user/fetchUserPoints2', async (
  _, { getState, rejectWithValue }
) => {
  try {
    const state = getState() as { user: IUSER; };
    if (isMockHomeworkContext() || !state.user.userPermission.isLogin) {
      return rejectWithValue(null);
    }
    const res = await getRemainingPoints();
    if (res.code !== 200) {
      return rejectWithValue(null);
    }
    return res.data;
  } catch (e) {
    return rejectWithValue(null);
  }

});

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    subtrackPointsLocal: (state, action: { payload: number; }) => {
      state.userPermission.points = state.userPermission.points - action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStudentInfoAsync.rejected,(state, {payload})=>{
        state.inited = true;

      })
      .addCase(getStudentInfoAsync.fulfilled,(state, {payload})=>{
        state.inited = true;
        if (payload) {
          state.userId = payload.account.split('-').pop() || payload.mobile || '';
          state.userName = payload.name;
          state.avatar = payload.head_sculpture || '';
          state.businessBrand = payload.business_brand?.logo_url
            ? {
              orgKey: payload.business_brand.org_key,
              logoUrl: payload.business_brand.logo_url,
            }
            : undefined;

          state.userPermission.isLogin = payload.isLogin;

          const vipAccess = payload.vip_access;
          const isWritingVip = vipAccess ? vipAccess.writing?.is_vip === true : payload.expiration_time > Date.now() / 1000;
          const isReadingVip = vipAccess ? vipAccess.reading?.is_vip === true : payload.reading_expiration_time > Date.now() / 1000;
          const isListening = vipAccess ? vipAccess.listening?.is_vip === true : payload.listening_expiration_time > Date.now() / 1000;
          state.userPermission.isVip = isWritingVip || isReadingVip || isListening;
          state.userPermission.writing.isVip = isWritingVip;
          state.userPermission.reading.isVip = isReadingVip;
          state.userPermission.listening.isVip = isListening;

          state.userPermission.expirationTime = Math.max(payload.expiration_time, payload.reading_expiration_time, payload.listening_expiration_time);

          state.vipDays = payload.vip_days;
          state.readingVipDays = payload.reading_vip_days;
          state.listeningVipDays = payload.listening_vip_days;

          state.targetScore = {
            writing: payload.target_score,
            listening: payload.target_score_listen,
            reading: payload.target_score_read,
          };
        }
        
      })
      .addCase(fetchUserPoints2.fulfilled, (state, action) => {
        if (action.payload) {
          state.userPermission.points = action.payload.remaining_points;
        }
      });
  },
});

export const UserActions = userSlice.actions;
